import React, { useEffect, useState } from 'react';
import { API_BASE } from '../utils/jobIntake';

// Part B: the candidate answers an interview round invite — no login, the signed link is the credential. Opening the
// page records nothing (mail scanners follow links); the candidate confirms Accept, Decline (optional reason + note) or
// Request reschedule (2–3 preferred time windows + a note). Each answer works once per invite; a reschedule by the
// recruiter sends a new invite and these links stop working.
const ACTIONS = [['accept', 'Accept'], ['decline', 'Decline'], ['reschedule', 'Request reschedule']];
const box = 'w-full px-3 py-2 text-sm border border-[#E6E9F0] rounded-lg bg-white text-[#0F172A] focus:outline-none focus:border-[#0066CC]';
const lbl = 'block text-xs font-semibold text-[#334155] mb-1';
const emptyWindow = () => ({ date: '', from: '', to: '' });

async function post(path, body) {
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  let data = null;
  try { data = await res.json(); } catch (e) { data = null; }
  if (!res.ok) {
    const err = new Error((data && typeof data.detail === 'string' && data.detail) || `Something went wrong (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export default function InterviewRoundResponsePage() {
  const q = new URLSearchParams(window.location.search);
  const t = q.get('t') || '';
  const [action, setAction] = useState(ACTIONS.some(([k]) => k === q.get('a')) ? q.get('a') : 'accept');
  const [d, setD] = useState(null);
  const [fatal, setFatal] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [windows, setWindows] = useState([emptyWindow(), emptyWindow()]);
  useEffect(() => { post('/api/public/interview-response/view', { t }).then(setD).catch((e) => setFatal(e.message)); }, [t]);
  const setW = (i, k, v) => setWindows(windows.map((w, j) => (j === i ? { ...w, [k]: v } : w)));
  const filled = windows.filter((w) => w.date && w.from && w.to);
  const ready = action !== 'reschedule' || (filled.length >= 2 && filled.length === windows.length);
  const send = async () => {
    setBusy(true); setError('');
    try {
      const body = { t, action, note: note.trim() || null };
      if (action === 'decline') body.reason = reason || null;
      if (action === 'reschedule') body.windows = windows.map((w) => ({ start: `${w.date}T${w.from}`, end: `${w.date}T${w.to}` }));
      const out = await post('/api/public/interview-response', body);
      setD(out);
      setDone(action === 'accept' ? 'Thanks — you’ve accepted. See you then!' : action === 'decline' ? 'Thanks for letting us know — the team has been told.'
        : 'Thanks — the team will get back to you with a new time.');
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-[#F6F8FB] px-4 py-10 font-inter text-[#0F172A]">
      <main className="max-w-lg mx-auto bg-white rounded-2xl border border-[#E6E9F0] shadow-sm p-5 sm:p-6" data-testid="round-response-page">
        <p className="text-xs font-bold tracking-wide text-[#0066CC] uppercase">Interview invitation</p>
        {fatal && <p className="mt-3 text-sm text-[#991B1B]" role="alert" data-testid="response-fatal">{fatal}</p>}
        {!d && !fatal && <p className="mt-3 text-sm text-[#64748B]">Loading…</p>}
        {d && <>
          <h1 className="text-xl font-extrabold mt-1">{d.round_name} — {d.job_title}</h1>
          <p className="text-sm text-[#475569] mt-1">{d.company ? `${d.company} · ` : ''}Hello {d.first_name}</p>
          <dl className="mt-4 text-sm space-y-1.5" data-testid="response-facts">
            <div><dt className="inline font-semibold">When: </dt><dd className="inline">{d.when} · {d.duration_minutes} min</dd></div>
            <div><dt className="inline font-semibold">How: </dt><dd className="inline">{d.mode_label}{d.meeting_link ? <> — <a className="text-[#0066CC] underline break-all" href={d.meeting_link} target="_blank" rel="noopener noreferrer">{d.provider || 'join link'}</a></> : d.location ? ` — ${d.location}` : ''}</dd></div>
            {d.onsite && <div data-testid="response-onsite"><dt className="inline font-semibold">Arrive: </dt><dd className="inline">by {d.onsite.arrive_by}
              {d.onsite.host ? ` — ask for ${d.onsite.host}` : ''} (<a className="text-[#0066CC] underline" href={d.onsite.maps_url} target="_blank" rel="noopener noreferrer">map</a>)
              {d.onsite.room ? ` · room ${d.onsite.room}` : ''}{d.onsite.reception_contact ? ` · reception ${d.onsite.reception_contact}` : ''}
              {d.onsite.entry_notes ? <span className="block text-[#475569]">{d.onsite.entry_notes}</span> : null}
              <span className="block text-[#475569]">Please bring a photo ID.</span></dd></div>}
            {d.panel.length > 0 && <div><dt className="inline font-semibold">With: </dt><dd className="inline">{d.panel.filter(Boolean).join(', ')}</dd></div>}
            <div><dt className="inline font-semibold">Your answer: </dt><dd className="inline" data-testid="response-status">{d.status_label}</dd></div>
          </dl>
          {done ? <p className="mt-5 text-sm font-semibold text-[#166534]" role="status" data-testid="response-done">{done}</p> : <>
            <div className="flex flex-wrap gap-1.5 mt-5" role="radiogroup" aria-label="Your answer">
              {ACTIONS.map(([k, l]) => (
                <button key={k} type="button" role="radio" aria-checked={action === k} onClick={() => { setAction(k); setError(''); }} data-testid={`response-${k}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${action === k ? 'border-[#0066CC] bg-[#0066CC] text-white' : 'border-[#E6E9F0] bg-white text-[#334155]'}`}>{l}</button>))}
            </div>
            {action === 'decline' && <label className="block mt-4"><span className={lbl}>Reason (optional)</span>
              <select className={box} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="response-reason">
                <option value="">Prefer not to say</option>{Object.entries(d.reasons).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>}
            {action === 'reschedule' && <fieldset className="mt-4"><legend className={lbl}>Times that work for you ({d.timezone}) — 2 or 3</legend>
              {windows.map((w, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 mb-2" data-testid={`response-window-${i}`}>
                  <input type="date" aria-label={`Window ${i + 1} date`} className={box} value={w.date} onChange={(e) => setW(i, 'date', e.target.value)} />
                  <input type="time" aria-label={`Window ${i + 1} from`} className={box} value={w.from} onChange={(e) => setW(i, 'from', e.target.value)} />
                  <input type="time" aria-label={`Window ${i + 1} to`} className={box} value={w.to} onChange={(e) => setW(i, 'to', e.target.value)} />
                </div>))}
              {windows.length < 3 && <button type="button" className="text-sm font-semibold text-[#0066CC]" onClick={() => setWindows([...windows, emptyWindow()])} data-testid="response-add-window">+ Add a third time</button>}
            </fieldset>}
            {action !== 'accept' && <label className="block mt-4"><span className={lbl}>Note to the team (optional)</span>
              <textarea className={box} rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} data-testid="response-note" /></label>}
            {error && <p className="mt-3 text-sm text-[#991B1B]" role="alert" data-testid="response-error">{error}</p>}
            <button type="button" className="mt-5 w-full sm:w-auto px-4 py-2.5 rounded-lg bg-[#0066CC] text-white text-sm font-semibold disabled:opacity-50"
              disabled={busy || !ready} onClick={send} data-testid="response-send">
              {action === 'accept' ? 'Confirm — I’ll attend' : action === 'decline' ? 'Decline the interview' : 'Send my preferred times'}</button>
          </>}
        </>}
      </main>
    </div>
  );
}
